import type {
  ForkStatus,
  UnpausedForkStatus,
} from "@gearbox-protocol/deploy-tools-shared";
import {
  Button,
  CopyToClipboardButton,
  EtherscanProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@gearbox-protocol/deploy-tools-ui";
import { getChain } from "@gearbox-protocol/sdk/onchain";
import { QueryClientProvider } from "@tanstack/react-query";
import { AdminOnly } from "api";
import ForkDropdownMenu from "components/fork-dropdown";
import NavBar from "components/NavBar";
import { ConnectKitButton, ConnectKitProvider } from "connectkit";
import type { FC, PropsWithChildren } from "react";
import { useMemo } from "react";
import { Outlet, useLoaderData } from "react-router-dom";
import { WagmiProvider } from "wagmi";
import { ForkStatusContext } from "./context";
import { SDKProvider } from "./sdk-context";
import useForkNav from "./useForkNav";
import WalletStateWarnings from "./WalletStateWarnings";
import { buildWagmiConfig, queryClient, shouldReconnectOnMount } from "./web3";

const ForkRoute: FC = () => {
  const fork = useLoaderData() as ForkStatus;

  return (
    <ForkStatusContext.Provider value={fork}>
      <EtherscanProvider value={getChain(fork.options.network).id}>
        {!fork.paused ? (
          <ForkWeb3Provider fork={fork as UnpausedForkStatus}>
            <SDKProvider>
              <ForkRouteBody />
            </SDKProvider>
          </ForkWeb3Provider>
        ) : (
          <ForkRouteBody />
        )}
      </EtherscanProvider>
    </ForkStatusContext.Provider>
  );
};

const ForkWeb3Provider: FC<
  { fork: UnpausedForkStatus } & PropsWithChildren
> = ({ fork, children }) => {
  const config = useMemo(() => buildWagmiConfig(fork), [fork]);
  const reconnectOnMount = useMemo(() => shouldReconnectOnMount(fork), [fork]);

  return (
    <WagmiProvider
      config={config}
      key={fork.executionId}
      reconnectOnMount={reconnectOnMount}
    >
      <QueryClientProvider client={queryClient}>
        <ConnectKitProvider>{children}</ConnectKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

const ForkRouteBody: FC = () => {
  const fork = useLoaderData() as ForkStatus;
  const nav = useForkNav(fork);

  const { alias, url, options, paused } = fork;
  let permalink = url;
  let status = paused ? "⏸️" : "";
  let title = alias;
  let isActive = false;
  if (!paused) {
    const unpaused = fork as UnpausedForkStatus;
    isActive = unpaused.isActive;
    permalink = url.replaceAll(alias ?? "???", unpaused.executionId);
    status = isActive ? "" : "☠️ ";
    title = alias || unpaused.executionId;
  }

  return (
    <>
      <div className="flex flex-col rounded-xl bg-background p-4 gap-y-2 grow-0">
        <div className="flex flex-row items-center justify-between">
          <h2>{`${status} Fork ${title}`}</h2>
          <div className="flex flex-row items-center gap-x-2">
            {!paused && isActive && (
              <>
                <WalletStateWarnings />
                <ConnectKitButton.Custom>
                  {({
                    isConnected,
                    isConnecting,
                    show,
                    truncatedAddress,
                    ensName,
                  }) => (
                    <Button
                      variant="outline"
                      onClick={show}
                      disabled={isConnecting}
                      className="text-slate-200"
                    >
                      {isConnected
                        ? (ensName ?? truncatedAddress)
                        : "Connect Wallet"}
                    </Button>
                  )}
                </ConnectKitButton.Custom>
              </>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <CopyToClipboardButton text={permalink} />
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy permalink to this execution</p>
              </TooltipContent>
            </Tooltip>

            <AdminOnly>
              <ForkDropdownMenu fork={fork} />
            </AdminOnly>
          </div>
        </div>

        {options.description && (
          <p className="text-slate-400">{options.description}</p>
        )}

        {!paused && <NavBar items={nav} pattern="/forks/:executionId" />}
      </div>

      <Outlet />
    </>
  );
};

export default ForkRoute;
